/**
 * AGENT-EVAL-1 — per-agent observability + scoring schemas.
 */
import { z } from 'zod';

export const AgentEvalWindowKind = z.enum(['rolling-7d', 'rolling-30d']);

export const AgentEvalAnomalyKind = z.enum([
  'cost-spike',
  'abstention-spike',
  'dead-letter-spike',
  'confidence-collapse',
  'call-volume-anomaly',
]);

export const AgentEvalSeverity = z.enum(['advisory', 'warning', 'critical']);

export const AgentEvalScorecard = z.object({
  id: z.string(),                          // ULID
  tenantId: z.string().default('platform'),
  agentId: z.string(),                     // FK → agent_registry
  windowKind: AgentEvalWindowKind,
  computedAt: z.string(),
  windowStartAt: z.string(),
  callCount: z.number().int().min(0),
  outcomeDistribution: z.object({
    delivered:      z.number().int().min(0),
    retried:        z.number().int().min(0),
    deadLettered:   z.number().int().min(0),
    quotaExceeded:  z.number().int().min(0),
    idempotentSkip: z.number().int().min(0),
  }),
  costPerCall: z.object({
    p50:   z.number().min(0),
    p95:   z.number().min(0),
    total: z.number().min(0),
  }),
  confidenceDistribution: z.object({
    p50:            z.number().min(0).max(1),
    p95:            z.number().min(0).max(1),
    abstentionRate: z.number().min(0).max(1), // KCRIT abstain count / call count
  }),
  highStakesCallCount: z.number().int().min(0),
  verticalBreakdown: z.object({
    cruise:       z.object({ accuracy: z.number().min(0).max(1), callCount: z.number().int() }).optional(),
    'spec-dist':  z.object({ accuracy: z.number().min(0).max(1), callCount: z.number().int() }).optional(),
    frozen:       z.object({ accuracy: z.number().min(0).max(1), callCount: z.number().int() }).optional(),
    metals:       z.object({ accuracy: z.number().min(0).max(1), callCount: z.number().int() }).optional(),
    'cold-pkg':   z.object({ accuracy: z.number().min(0).max(1), callCount: z.number().int() }).optional(),
  }).optional(),
  longContextMetrics: z.object({
    accuracy:     z.number().min(0).max(1),
    callCount:    z.number().int().min(0),
    driftPercent: z.number(),
    costRatio:    z.number().min(0),
  }).optional(),
  calibrationDeltas: z.object({
    cruise:      z.number().optional(),
    'spec-dist': z.number().optional(),
    frozen:      z.number().optional(),
    metals:      z.number().optional(),
    'cold-pkg':  z.number().optional(),
  }).optional(),
  substrateMembership: z.object({
    substrates:   z.array(z.string()),
    isHorizontal: z.boolean().default(false),
  }).optional(),
  softDeleted: z.boolean().default(false),
});

export const AgentEvalAnomaly = z.object({
  id: z.string(),                          // ULID
  tenantId: z.string().default('platform'),
  agentId: z.string(),
  detectedAt: z.string(),
  anomalyKind: AgentEvalAnomalyKind,
  severity: AgentEvalSeverity,
  factorsCited: z.array(z.string()),
  windowKind: AgentEvalWindowKind,
  resolvedAt: z.string().nullable().default(null),
  resolution: z.string().nullable().default(null),
  softDeleted: z.boolean().default(false),
});
